import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type PublicClip } from '@air/api';
import { type ClipAndBoardListOptions, type PublicBoard } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { useAppliedSortField } from '~/hooks/search/useAppliedSortField';
import { useCurrentBoardIdSelector } from '~/hooks/useCurrentBoardIdSelector';
import { setSortingFieldAction } from '~/store/configViews/actions';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { useBoardChildTableViewItems } from '~/swr-hooks/gallery/tableView/useBoardChildTableViewItems';
import { usePublicTableViewBoardsDataFetcher } from '~/swr-hooks/gallery/tableView/usePublicTableViewBoardsDataFetcher';
import { publicTableViewClipsDataFetcher, publicTableViewDataFetcher } from '~/swr-hooks/gallery/tableView/utils';
import { type UseGalleryMixedData } from '~/swr-hooks/gallery/types';
import { useCanShowBoards } from '~/swr-hooks/gallery/useCanShow';
import { useBoardSearchParams } from '~/swr-hooks/search/useBoardSearchParams';
import { useClipSearchParams } from '~/swr-hooks/search/useClipSearchParams';
import { getTypesToFetchFromParams } from '~/swr-hooks/search/utils';
import { replaceInternalAnalyticsTableSort } from '~/utils/internalAnalytics/tableMetrics';

export type UsePublicTableViewItemsProps = {
  shouldUpdateMediaPlayer?: boolean;
  /**
   * The collection the board is being viewed within, so every fetch scopes to that collection —
   * collection surfaces always send it (the active sub-page's collection, or the link's root),
   * never leaning on the backend's root-collection default. Absent ONLY on the standalone board
   * link, which has no collection; its requests stay byte-identical to the pre-sub-pages shapes.
   */
  collectionId?: string;
};

// biome-ignore lint/plugin: pre-existing function declaration; out of scope for this selector swap
export function usePublicTableViewItems({
  shouldUpdateMediaPlayer = true,
  collectionId,
}: UsePublicTableViewItemsProps = {}): UseGalleryMixedData<PublicBoard, PublicClip> {
  const dispatch = useDispatch();
  const { publicTableViewBoardsDataFetcher } = usePublicTableViewBoardsDataFetcher();
  const { appliedSortFieldDirection: currentSortFieldDirection, appliedSortFieldName: currentSortFieldName } =
    useAppliedSortField();
  const publicSort = replaceInternalAnalyticsTableSort(currentSortFieldName, currentSortFieldDirection);
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const { shortId, presentationSettings } = useShortIdContext();
  const isExpandNestedDisabled = presentationSettings?.lockedControls?.expandNested === true;
  const currentBoardId = useCurrentBoardIdSelector();
  const { typesParam } = useFilterParams();

  const clipsFetchParams = useClipSearchParams();
  clipsFetchParams.types = getTypesToFetchFromParams(typesParam);
  const boardsFetchParams = useBoardSearchParams();

  const { canShowBoards } = useCanShowBoards({ isExpandNestedDisabled });

  useEffect(() => {
    if (publicSort.sortFieldName === currentSortFieldName) {
      return;
    }

    dispatch(
      setSortingFieldAction({
        sortingFieldName: publicSort.sortFieldName,
        direction: publicSort.sortDirection,
      }),
    );
  }, [currentSortFieldName, dispatch, publicSort.sortDirection, publicSort.sortFieldName]);

  const fetchParams: Partial<ClipAndBoardListOptions> = {
    ...clipsFetchParams,
    ...boardsFetchParams,
    sortBy: undefined, // ensure we use the new sort fieldName and direction
  };

  const dataCacheKey = {
    ...fetchParams,
    ...typesParam,
    canShowBoards,
    currentSortFieldName: publicSort.sortFieldName,
    currentSortFieldDirection: publicSort.sortDirection,
    shortId,
    ...(collectionId ? { collectionId } : {}),
  };

  const fetcherParams = {
    params: fetchParams,
    sortFieldName: publicSort.sortFieldName,
    sortDirection: publicSort.sortDirection,
  };

  return useBoardChildTableViewItems({
    dataCacheKey,
    enabled: !!currentViewTypeName && !!shortId && !!currentBoardId,
    queryFn: (pageParam) => {
      if (!shortId) {
        throw new Error('shortId is required');
      }
      if (!currentBoardId) {
        throw new Error('currentBoardId is required');
      }

      if (!canShowBoards) {
        return publicTableViewClipsDataFetcher({
          ...fetcherParams,
          parentId: currentBoardId,
          shortId,
          cursor: pageParam || null,
          collectionId,
        });
      }

      if (typesParam.length === 1 && typesParam[0] === 'boards') {
        return publicTableViewBoardsDataFetcher({
          ...fetcherParams,
          shortId,
          params: boardsFetchParams,
          parentId: currentBoardId,
          cursor: pageParam || null,
          collectionId,
        });
      }

      return publicTableViewDataFetcher({ ...fetcherParams, shortId, cursor: pageParam, collectionId });
    },
    shouldUpdateMediaPlayer,
  });
}
